import type { Tenant } from "@albertine/tenant-config";
import { ConciergeBell } from "lucide-react";
import type { ReactNode } from "react";

import revolutTenantAvatar from "../assets/revolut-tenant-avatar.svg";
import { cn } from "../lib/cn";
import { useDesignSystemTenant } from "../providers/DesignSystemProvider";

type TenantAvatarSize = "xlarge" | "large" | "medium" | "small";

export type TenantAvatarProps = {
    className?: string;
    size?: TenantAvatarSize;
};

export function TenantAvatar({ className, size = "medium" }: TenantAvatarProps) {
    const tenant = useDesignSystemTenant();
    const emblem = tenantAvatarEmblemByTenant[tenant](size);

    return (
        <div
            className={cn(
                "flex items-center justify-center rounded-full bg-component-avatar-agent",
                "leading-[0] [&_img]:h-auto",
                tenantAvatarSizeClass[size],
                className,
            )}
        >
            {emblem}
        </div>
    );
}

const CONCIERGE_BELL_IMG =
    "https://ik.imagekit.io/albertine/share/hb-tenant-avatar.svg";

const tenantAvatarEmblemByTenant: Record<
    Tenant,
    (size: TenantAvatarSize) => ReactNode
> = {
    albertine: () => (
        <img
            src="https://ik.imagekit.io/albertine/share/albertine-emblem.svg"
            alt="Albertine"
        />
    ),
    hugoboss: () => <img src={CONCIERGE_BELL_IMG} alt="Hugo Boss XP Concierge" />,
    mastercard: () => (
        <img src={CONCIERGE_BELL_IMG} alt="Mastercard Digital Concierge" />
    ),
    carlo: (size) => (
        <ConciergeBell
            aria-hidden="true"
            className={cn("text-component-avatar-text", tenantAvatarIconClass[size])}
        />
    ),
    "visa-i2": () => (
        <img
            src="https://ik.imagekit.io/albertine/share/visa-i2-emblem.svg"
            alt="Infinite Private Concierge by Albertine"
        />
    ),
    revolut: () => (
        <img
            src={revolutTenantAvatar}
            alt="Albertine for Revolut"
            className="brightness-0 invert"
        />
    ),
};

const tenantAvatarSizeClass: Record<TenantAvatarSize, string> = {
    small: "size-24 min-h-24 min-w-24 [&_img]:w-[10px]",
    medium: "size-32 min-h-32 min-w-32 [&_img]:w-12",
    large: "size-40 min-h-40 min-w-40 [&_img]:w-[14px]",
    xlarge: "size-48 min-h-48 min-w-48 [&_img]:w-16",
};

const tenantAvatarIconClass: Record<TenantAvatarSize, string> = {
    small: "size-12",
    medium: "size-16",
    large: "size-[18px]",
    xlarge: "size-24",
};
